import type React from 'react';
import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { lbReOnboardingAnchorKey, onboardingHints } from '../../constants';
import { SnackbarContext } from '../../state/SnackbarContextProvider';
import { safeLocalStorage } from '../../utils/safeStorage';
import useIsLBUser from '../../utils/useIsLBUser';
import useOnboardingDialog from '../../utils/useOnboardingDialog';
import ReOnboardingDialog, { type ReOnboardingStep } from './ReOnboardingDialog';
import useShouldShowLBReOnboardingDialog from './useShouldShowLBReOnboardingDialog';

const STEPS: ReOnboardingStep[] = [
	'schulfaecher',
	'wichtigkeit',
	'roles',
	'schule',
	'bundesland',
	'schulstufe',
];

const LBReOnboardingDialog: React.FC = () => {
	const { t } = useTranslation();
	const isLBUser = useIsLBUser();
	const { markDialogAsSeen } = useOnboardingDialog(onboardingHints.lbReOnboardingDialog);
	const { openSnackbar } = useContext(SnackbarContext);

	// Seed gap anchor on first mount for LB user (replaces previous anchor from Welcome dialog dismissal).
	useEffect(() => {
		if (isLBUser && !safeLocalStorage.getItem(lbReOnboardingAnchorKey)) {
			safeLocalStorage.setItem(lbReOnboardingAnchorKey, String(Date.now()));
		}
	}, [isLBUser]);

	const shouldShow = useShouldShowLBReOnboardingDialog();

	const [isOpen, setIsOpen] = useState(false);

	// biome-ignore lint/correctness/useExhaustiveDependencies: pre-Biome hook deps kept as-is; changing the deps list alters effect timing
	useEffect(() => {
		if (shouldShow && !isOpen) {
			setIsOpen(true);
		}
	}, [shouldShow]);

	if (!shouldShow && !isOpen) return null;

	const handleClose = () => {
		markDialogAsSeen();
		setIsOpen(false);
	};

	const handleComplete = () => {
		handleClose();
		openSnackbar({
			type: 'success',
			message: t('Your profile has been updated successfully!'),
		});
	};

	return (
		<ReOnboardingDialog
			open={isOpen}
			steps={STEPS}
			onComplete={handleComplete}
			onClose={handleClose}
			dataTestId="lb-reonboarding-dialog"
		/>
	);
};

export default LBReOnboardingDialog;
